import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import api from '../../services/api';

// ── Module ohne Seiten-UI ───────────────────────────────────
const NO_PAGE_BIND = new Set(['payment']);

// ── Seiten-spezifische Felder ───────────────────────────────
const PAGE_FIELDS = {
  home: [
    { k: 'hero_title',  l: 'Hero – Überschrift',   type: 'text',    hint: 'Große Hauptüberschrift im Hero-Bereich' },
    { k: 'hero_sub',    l: 'Hero – Untertitel',     type: 'wysiwyg', hint: '' },
    { k: 'cta_text',    l: 'Hero – Button-Text',    type: 'text',    hint: 'z.B. "Kostenlosen Beta-Platz sichern"' },
    { k: 'stats_1n',    l: 'Statistik 1 – Zahl',    type: 'text',    hint: 'z.B. "10.000+"' },
    { k: 'stats_1l',    l: 'Statistik 1 – Label',   type: 'text',    hint: 'z.B. "Rezepte digitalisiert"' },
    { k: 'stats_2n',    l: 'Statistik 2 – Zahl',    type: 'text',    hint: '' },
    { k: 'stats_2l',    l: 'Statistik 2 – Label',   type: 'text',    hint: '' },
    { k: 'stats_3n',    l: 'Statistik 3 – Zahl',    type: 'text',    hint: '' },
    { k: 'stats_3l',    l: 'Statistik 3 – Label',   type: 'text',    hint: '' },
  ],
  upload: [
    { k: 'page_title',  l: 'Seitentitel',            type: 'text',    hint: 'Überschrift über dem Upload-Formular' },
    { k: 'hint',        l: 'Hinweistext',             type: 'wysiwyg', hint: 'Kurzer Infotext für den Nutzer' },
  ],
  detail: [
    { k: 'related_title', l: '"Ähnliche Rezepte" – Überschrift', type: 'text', hint: '' },
  ],
  impressum: [
    { k: 'content',     l: 'Inhalt',                 type: 'wysiwyg', hint: '' },
  ],
  datenschutz: [
    { k: 'content',     l: 'Inhalt',                 type: 'wysiwyg', hint: '' },
  ],
};
const DEFAULT_FIELDS = [
  { k: 'page_title', l: 'Seitentitel',  type: 'text',    hint: '' },
  { k: 'content',    l: 'Inhalt',       type: 'wysiwyg', hint: '' },
];

// ── Default-Inhalte für leere Seiten ────────────────────────
const PAGE_DEFAULTS = {
  impressum: {
    content: `<h2>Betreiber</h2>
<p>[Vollständiger Name]<br>[Straße + Hausnummer]<br>[PLZ Ort], Österreich<br>E-Mail: [kontakt@strixly.at]</p>
<h2>Haftungsausschluss</h2>
<p>Die Inhalte dieser Website wurden sorgfältig erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir keine Gewähr übernehmen.</p>
<h2>Urheberrecht</h2>
<p>Die durch den Betreiber erstellten Inhalte und Werke auf dieser Website unterliegen dem österreichischen Urheberrecht.</p>`,
  },
  datenschutz: {
    content: `<h2>1. Verantwortlicher</h2>
<p>[Name]<br>[Adresse]<br>[E-Mail]</p>
<h2>2. Welche Daten wir speichern</h2>
<ul><li><strong>Benutzerkonto:</strong> Benutzername, Passwort (verschlüsselt), E-Mail-Adresse</li><li><strong>Rezeptdaten:</strong> Hochgeladene Bilder und daraus extrahierte Texte</li><li><strong>Nutzungsdaten:</strong> Anzahl der Scans (für Quota-Verwaltung)</li></ul>
<h2>3. Einsatz von KI-Diensten (Google Gemini API)</h2>
<p>Zur Texterkennung in Bildern nutzen wir die Google Gemini API (Google LLC, USA). Hochgeladene Bilder werden zur Verarbeitung temporär an Google übermittelt. Google verarbeitet diese Daten auf Grundlage von Standardvertragsklauseln (Art. 46 DSGVO).</p>
<h2>4. Speicherdauer</h2>
<p>Deine Daten werden gespeichert solange dein Konto besteht. Du kannst jederzeit die Löschung deines Kontos und aller zugehörigen Daten verlangen.</p>
<h2>5. Deine Rechte (DSGVO Art. 15–22)</h2>
<ul><li>Recht auf Auskunft über gespeicherte Daten</li><li>Recht auf Berichtigung oder Löschung</li><li>Recht auf Einschränkung der Verarbeitung</li><li>Recht auf Datenübertragbarkeit</li><li>Beschwerderecht bei der österreichischen Datenschutzbehörde (dsb.gv.at)</li></ul>
<h2>6. Kontakt</h2>
<p>Bei Fragen zum Datenschutz: <a href="mailto:kontakt@strixly.at">kontakt@strixly.at</a></p>`,
  },
  agb: {
    content: `<h2>1. Geltungsbereich</h2>
<p>Diese Allgemeinen Geschäftsbedingungen gelten für die Nutzung der Plattform Strixly (strixly.at).</p>
<h2>2. Nutzung der Plattform</h2>
<p>Die Nutzung ist kostenlos. Ein Anspruch auf dauerhaften Zugang besteht nicht.</p>
<h2>3. Pflichten der Nutzer</h2>
<p>Nutzer verpflichten sich, keine rechtswidrigen Inhalte hochzuladen und die Rechte Dritter zu achten.</p>
<h2>4. Haftung</h2>
<p>Der Betreiber haftet nicht für Schäden, die durch die Nutzung der Plattform entstehen.</p>`,
  },
  kontakt: {
    content: `<h2>Kontakt</h2>
<p>E-Mail: <a href="mailto:kontakt@strixly.at">kontakt@strixly.at</a></p>
<h2>Support</h2>
<p>Bei Fragen oder Problemen erreichst du uns per E-Mail. Wir antworten in der Regel innerhalb von 48 Stunden.</p>`,
  },
};

const TYPE_COLORS = { banner: 'var(--admin-accent)', text: 'var(--admin-text-muted)', html: 'var(--admin-link)', ad: 'var(--admin-link)' };

// ── WYSIWYG ─────────────────────────────────────────────────
function WysiwygField({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });
  const synced = useRef(false);
  useEffect(() => {
    if (editor && !synced.current && value) {
      editor.commands.setContent(value, false);
      synced.current = true;
    }
  }, [editor, value]);

  const tb = (fn, label, active) => (
    <button type="button" onMouseDown={e => { e.preventDefault(); fn(); }}
      style={{ background: active ? 'var(--admin-border)' : 'none', border: '1px solid var(--admin-border)', color: active ? 'var(--admin-text)' : 'var(--admin-text-muted)', borderRadius: 3, padding: '3px 8px', fontSize: '.72rem', cursor: 'pointer', minWidth: 28 }}>
      {label}
    </button>
  );
  if (!editor) return null;
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '5px 8px', background: 'var(--admin-bg)', borderRadius: '5px 5px 0 0', border: '1px solid var(--admin-border)', borderBottom: 'none' }}>
        {tb(() => editor.chain().focus().toggleBold().run(),                     'B',  editor.isActive('bold'))}
        {tb(() => editor.chain().focus().toggleItalic().run(),                   'I',  editor.isActive('italic'))}
        {tb(() => editor.chain().focus().toggleHeading({ level: 2 }).run(),      'H2', editor.isActive('heading', { level: 2 }))}
        {tb(() => editor.chain().focus().toggleHeading({ level: 3 }).run(),      'H3', editor.isActive('heading', { level: 3 }))}
        {tb(() => editor.chain().focus().toggleBulletList().run(),               '•',  editor.isActive('bulletList'))}
        {tb(() => editor.chain().focus().toggleOrderedList().run(),              '1.', editor.isActive('orderedList'))}
        {tb(() => editor.chain().focus().toggleBlockquote().run(),               '❝',  editor.isActive('blockquote'))}
      </div>
      <EditorContent editor={editor}
        style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: '0 0 5px 5px', padding: '10px 12px', color: 'var(--admin-text)', fontSize: '.84rem', minHeight: 120 }} />
      <style>{`.tiptap{outline:none}.tiptap p{margin:0 0 .5em}.tiptap h2{color:var(--admin-link);margin:.7em 0 .3em;font-size:1.05rem}.tiptap h3{color:var(--admin-link);margin:.6em 0 .2em;font-size:.9rem}.tiptap ul,.tiptap ol{padding-left:1.4em;margin:.3em 0}.tiptap blockquote{border-left:3px solid var(--admin-border);margin:.4em 0;padding-left:.8em;color:var(--admin-text-muted)}`}</style>
    </div>
  );
}

// ── Haupt-Komponente ─────────────────────────────────────────
export default function ContentEditor() {
  const { slug } = useParams();

  // Seiten-Inhalte
  const [fields, setFields]     = useState({});   // { key: value }
  const [pageName, setPageName] = useState('');
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState('');

  // Sidebar: Regionen
  const [regions, setRegions]   = useState([]);   // alle Regionen für diese Seite + __all__

  // Sidebar: Module
  const [modules, setModules]   = useState([]);   // bindbare Module
  const [mpMap, setMpMap]       = useState({});   // { mod_name: [page_slugs] }

  const pageFields = PAGE_FIELDS[slug] || DEFAULT_FIELDS;

  useEffect(() => {
    Promise.all([
      api.getLayout(slug),
      api.getAllRegions().catch(() => ({ regions: [] })),
      api.getAllModules().catch(() => ({ modules: [] })),
      api.getModulePages().catch(() => ({ module_pages: {} })),
    ]).then(([ld, rd, md, mpd]) => {
      const lj = ld.layout?.layout_json || {};
      setPageName(ld.layout?.name || slug);
      const savedFields = lj.fields || {};
      // Wenn noch kein Inhalt gespeichert: Default-Text vorladen
      const defaults = PAGE_DEFAULTS[slug] || {};
      const merged = { ...defaults, ...savedFields };
      setFields(merged);

      // Nur Regionen die für diese Seite relevant sind
      setRegions((rd.regions || []).filter(r =>
        r.page_scope === '__all__' || r.page_scope === slug
      ));

      setModules((md.modules || []).filter(m => !NO_PAGE_BIND.has(m.name)));
      setMpMap(mpd.module_pages || {});
    }).catch(() => setError('Seite konnte nicht geladen werden.'));
  }, [slug]);

  // ── Felder speichern ──────────────────────────────────────
  async function save() {
    setSaving(true); setSaved(false);
    try {
      await api.saveLayout(slug, pageName, { fields }, false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError('Speichern fehlgeschlagen: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Region an/aus ────────────────────────────────────────
  async function toggleRegion(region) {
    try {
      await api.saveRegion(region.name, { ...region, enabled: !region.enabled });
      setRegions(prev => prev.map(r => r.name === region.name ? { ...r, enabled: !r.enabled } : r));
    } catch (e) { setError('Fehler: ' + e.message); }
  }

  // ── Modul-Bindung an/aus ─────────────────────────────────
  async function toggleModule(modName) {
    const current = mpMap[modName] || [];
    const isHere  = current.includes(slug);
    const next    = isHere ? current.filter(p => p !== slug) : [...current, slug];
    try {
      await api.setModulePages(modName, next);
      setMpMap(prev => ({ ...prev, [modName]: next }));
    } catch (e) { setError('Fehler: ' + e.message); }
  }

  // ── Styles ──────────────────────────────────────────────
  const C = {
    bg: 'var(--admin-bg)', card: 'var(--admin-bg-alt)', border: 'var(--admin-border)',
    text: 'var(--admin-text)', muted: 'var(--admin-text-dim)', accent: 'var(--admin-btn-primary)',
  };

  if (!Object.keys(fields).length && !error && pageName === '') {
    return <div style={{ padding: 40, color: C.muted, fontFamily: 'Inter,sans-serif' }}>Lade…</div>;
  }

  return (
    <div style={{ padding: '24px 28px', fontFamily: 'Inter,sans-serif', maxWidth: 1200 }}>

      {/* ── Kopfzeile ─────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: '.75rem', color: C.muted, marginBottom: 4 }}>
            <Link to="/admin/pages" style={{ color: 'var(--admin-link)', textDecoration: 'none' }}>← Seiten</Link>
            <span style={{ margin: '0 6px' }}>/</span>
            <span style={{ color: C.text }}>{pageName}</span>
            <code style={{ fontSize: '.65rem', color: C.muted, marginLeft: 8 }}>{slug}</code>
          </div>
          <h1 style={{ fontSize: '1.3rem', color: C.text, fontWeight: 600, margin: 0 }}>Seite bearbeiten</h1>
        </div>
        <button
          onClick={save} disabled={saving}
          style={{ background: saved ? 'var(--admin-saved)' : C.accent, border: 'none', color: '#fff', borderRadius: 6, padding: '9px 22px', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', minWidth: 130 }}
        >
          {saving ? 'Speichert…' : saved ? '✓ Gespeichert' : '💾 Speichern'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 6, padding: '10px 14px', color: 'var(--admin-danger)', fontSize: '.78rem', marginBottom: 20 }}>
          {error}
        </div>
      )}

      {/* ── 2-Spalten-Layout ──────────────────────────────── */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* ── LINKE SPALTE: Inhaltsfelder ────────────────── */}
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          {pageFields.map(f => (
            <div key={f.k} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', background: 'var(--admin-bg-dark)', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: '.83rem', color: C.text }}>{f.l}</span>
                <span style={{ fontSize: '.62rem', padding: '1px 7px', borderRadius: 10, fontWeight: 600, background: f.type === 'wysiwyg' ? 'color-mix(in srgb, var(--admin-link) 15%, transparent)' : 'color-mix(in srgb, var(--admin-text-muted) 10%, transparent)', color: f.type === 'wysiwyg' ? 'var(--admin-link)' : 'var(--admin-text-muted)', border: f.type === 'wysiwyg' ? '1px solid color-mix(in srgb, var(--admin-link) 20%, transparent)' : '1px solid color-mix(in srgb, var(--admin-text) 9%, transparent)' }}>
                  {f.type}
                </span>
                {f.hint && <span style={{ fontSize: '.65rem', color: C.muted, marginLeft: 4 }}>{f.hint}</span>}
              </div>
              <div style={{ padding: '14px 16px' }}>
                {f.type === 'wysiwyg' ? (
                  <WysiwygField
                    value={fields[f.k] || ''}
                    onChange={v => setFields(prev => ({ ...prev, [f.k]: v }))}
                  />
                ) : (
                  <input
                    style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 5, color: C.text, padding: '8px 11px', fontSize: '.84rem', boxSizing: 'border-box', outline: 'none' }}
                    value={fields[f.k] || ''}
                    placeholder={f.hint || f.l + '…'}
                    onChange={e => setFields(prev => ({ ...prev, [f.k]: e.target.value }))}
                  />
                )}
              </div>
            </div>
          ))}

          {pageFields.length === 0 && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '40px 24px', textAlign: 'center', color: C.muted, fontSize: '.82rem' }}>
              Für diese Seite sind keine bearbeitbaren Felder definiert.
            </div>
          )}
        </div>

        {/* ── RECHTE SIDEBAR ────────────────────────────── */}
        <div style={{ width: 280, flexShrink: 0 }}>

          {/* Regionen */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: 'var(--admin-bg-dark)', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontWeight: 600, fontSize: '.78rem', color: C.text }}>📍 Regionen</span>
              <span style={{ fontSize: '.62rem', color: C.muted, marginLeft: 6 }}>Inhalt-Slots auf dieser Seite</span>
            </div>
            <div style={{ padding: '10px 14px' }}>
              {regions.length === 0 ? (
                <p style={{ color: C.muted, fontSize: '.72rem', margin: 0 }}>Keine Regionen für diese Seite definiert.</p>
              ) : regions.map(r => (
                <div key={r.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '.78rem', color: r.enabled ? C.text : C.muted, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.label}
                    </div>
                    <div style={{ display: 'flex', gap: 5, marginTop: 2 }}>
                      <span style={{ fontSize: '.58rem', padding: '1px 5px', borderRadius: 8, background: `color-mix(in srgb, ${TYPE_COLORS[r.type] || 'var(--admin-text-muted)'} 13%, transparent)`, color: TYPE_COLORS[r.type] || 'var(--admin-text-muted)', border: `1px solid color-mix(in srgb, ${TYPE_COLORS[r.type] || 'var(--admin-text-muted)'} 27%, transparent)` }}>
                        {r.type}
                      </span>
                      {r.page_scope === '__all__' && (
                        <span style={{ fontSize: '.58rem', color: C.muted }}>alle Seiten</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleRegion(r)}
                    style={{ width: 38, height: 21, borderRadius: 11, border: 'none', cursor: 'pointer', background: r.enabled ? C.accent : 'var(--admin-text-dim)', position: 'relative', flexShrink: 0, transition: 'background .2s' }}
                  >
                    <div style={{ position: 'absolute', top: 2, left: r.enabled ? 19 : 2, width: 17, height: 17, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                  </button>
                </div>
              ))}
              <div style={{ marginTop: 6, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                <Link to="/admin/regions" style={{ fontSize: '.65rem', color: 'var(--admin-link)', textDecoration: 'none' }}>
                  + Regionen verwalten →
                </Link>
              </div>
            </div>
          </div>

          {/* Module */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: 'var(--admin-bg-dark)', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontWeight: 600, fontSize: '.78rem', color: C.text }}>🧩 Module</span>
              <span style={{ fontSize: '.62rem', color: C.muted, marginLeft: 6 }}>Sichtbar auf dieser Seite</span>
            </div>
            <div style={{ padding: '10px 14px' }}>
              {modules.map(mod => {
                const pages   = mpMap[mod.name] || [];
                const isHere  = pages.includes(slug);
                const noFilter = pages.length === 0;
                return (
                  <label key={mod.name} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isHere || noFilter}
                      onChange={() => toggleModule(mod.name)}
                      style={{ marginTop: 2, accentColor: C.accent, flexShrink: 0 }}
                    />
                    <div>
                      <div style={{ fontSize: '.78rem', color: C.text, fontWeight: 500 }}>{mod.label}</div>
                      <div style={{ fontSize: '.62rem', color: noFilter && !isHere ? C.muted : isHere ? 'var(--admin-success)' : 'var(--admin-danger)' }}>
                        {noFilter && !isHere ? 'überall aktiv (kein Filter)' : isHere ? 'aktiv auf dieser Seite' : 'auf anderen Seiten'}
                      </div>
                    </div>
                  </label>
                );
              })}
              <div style={{ marginTop: 4, paddingTop: 8, borderTop: `1px solid ${C.border}`, fontSize: '.6rem', color: C.muted, lineHeight: 1.4 }}>
                ✓ = Modul erscheint hier<br/>
                Kein Filter = Modul überall sichtbar
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
